import { Avatar, Box, Stack, Tooltip, styled } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ICommunicationLog, getCandidateSmsLogs } from 'wisestep-api';
import {
  timeSince,
  WsButton,
  WsColors,
  WsTitleBanner,
  WsTypoColors,
  WsTypography,
} from 'wisestep-base';

import { useConversationWidgetStore } from '../../utils/useConversationWidgetStore';

import SmsLogFilter from './components/SmsLogFilter';
import SmsLogsChatBubble from './components/SmsLogsChatBubble';
import SmsLogsZoomChatBubble from './components/SmsLogsZoomChatBubble';

const LayoutTheme = styled(Box)(() => ({
  maxWidth: '100%',
  maxHeight: 'xs',
  backgroundColor: WsColors.TertiaryShades50,
  padding: 2,
}));

const SmsLogs = () => {
  const [smsLogs, setSmsLogs] = useState<ICommunicationLog[]>([]);

  const [totalCount, setTotalCount] = useState<number>(0);

  const [page, setPage] = useState<number>(1);

  const setSmsLogsContactNumber = useConversationWidgetStore(
    (state) => state.setSmsLogsContactNumber
  );

  const navigate = useNavigate();
  const getSmsLogsConvertion = (data): ICommunicationLog[] =>
    data.map((log) => ({
      id: log.id,
      accountId: log.selectedAccount,
      serviceId: '',
      direction: log.direction,
      type: 'SMS',
      from: log.from,
      to: log.to,
      service: '',
      candidateId: log.candidateId,
      candidateName: '',
      interactionId: '',
      workAssignmentId: '',
      jobId: log.jobId,
      jobTitle: log.jobTitle,
      messageStatus: log.deliveryStatus,
      message: log.body,
      recruiterName: log.recruiterId,
      createdOn: log.createdOn,
      createdBy: log.recruiterId,
      modifiedOn: log.createdOn,
      modifiedBy: '',
      workspaceId: log.workSpaceId,
      workspaceName: '',
      isBulk: false,
      creditConsumed: log.creditConsumed,
      transcriptURL: '',
    }));

  const fetchSmsLogs = useCallback(async () => {
    try {
      const response = await getCandidateSmsLogs(page);

      setSmsLogs((prev) => [
        ...prev,
        ...getSmsLogsConvertion(response.data.searchResult?.content ?? []),
      ]);
      setTotalCount(response.data.total);
    } catch (err) {
      setSmsLogs([]);
      setTotalCount(0);
    }
  }, [page]);

  const fetchJobAndOpenModal = async (data) => {
    data.direction === 'INBOUND'
      ? setSmsLogsContactNumber(data.from)
      : setSmsLogsContactNumber(data.to);
    sessionStorage.setItem('jobsUrls', `/sms-logs`);
    navigate(`/candidate-layout/profile?id=${data.candidateId}&array=${[data.candidateId]}`);
  };

  const changeMessageStatusFilter = useCallback(
    (filter) => {
      if (filter.length > 0)
        setSmsLogs((prev) => prev.filter((log) => filter.includes(log.messageStatus)));
      else {
        setSmsLogs([]);
        fetchSmsLogs();
      }
    },
    [fetchSmsLogs]
  );

  const changeDirectionFilter = useCallback(
    (filter) => {
      if (filter) setSmsLogs((prev) => prev.filter((log) => log.direction === filter));
      else {
        setSmsLogs([]);
        fetchSmsLogs();
      }
    },
    [fetchSmsLogs]
  );

  useEffect(() => {
    fetchSmsLogs();
  }, [fetchSmsLogs]);

  return (
    <LayoutTheme>
      <WsTitleBanner title="Communication Logs" goBack={() => navigate('/')} />

      <SmsLogFilter
        setMessageStatusFilter={changeMessageStatusFilter}
        setDirectionFilter={changeDirectionFilter}
      />
      {smsLogs
        ?.filter((log) => log?.type === 'SMS')
        ?.map((log, key) => {
          if (log.service === 'ZOOM') {
            return (
              <Stack
                key={key}
                alignItems={log.direction === 'OUTBOUND' ? 'flex-end' : 'flex-start'}
                p={2}
              >
                <Stack direction={'row'} alignItems={'center'}>
                  {log.direction === 'INBOUND' && (
                    <Tooltip title={`${log.from} ${log.candidateName}`}>
                      <Avatar>{`${log?.candidateName
                        ?.split(' ')[0]
                        ?.substring(0, 1)} ${log?.candidateName
                        ?.split(' ')[1]
                        ?.substring(0, 1)}`}</Avatar>
                    </Tooltip>
                  )}
                  <SmsLogsZoomChatBubble
                    key={key}
                    message={log.message}
                    recruiterName={log.recruiterName ?? ''}
                    direction={log.direction}
                    onClick={() => fetchJobAndOpenModal(log)}
                  />
                  {log.direction === 'OUTBOUND' && (
                    <Tooltip title={`${log.from} ${log.modifiedBy}`}>
                      <Avatar>{`${log?.recruiterName?.substring(0, 1)}`}</Avatar>
                    </Tooltip>
                  )}
                </Stack>
                <Stack px={8}>
                  <WsTypography color={WsTypoColors.TERTIARY}>
                    {timeSince(log.createdOn?.toString() ?? '')}
                  </WsTypography>
                </Stack>
              </Stack>
            );
          } else {
            return (
              <Stack
                key={key}
                alignItems={log.direction === 'OUTBOUND' ? 'flex-end' : 'flex-start'}
                p={2}
              >
                <Stack direction={'row'} alignItems={'center'}>
                  {log.direction === 'INBOUND' && (
                    <Tooltip title={`${log.from} ${log.candidateName}`}>
                      <Avatar>{`${log?.candidateName
                        ?.split(' ')[0]
                        ?.substring(0, 1)} ${log?.candidateName
                        ?.split(' ')[1]
                        ?.substring(0, 1)}`}</Avatar>
                    </Tooltip>
                  )}
                  <SmsLogsChatBubble
                    key={key}
                    message={log.message}
                    recruiterName={log.recruiterName ?? ''}
                    candidateName={log.candidateName ?? ''}
                    direction={log.direction}
                    creditConsumed={log.creditConsumed}
                    messageStatus={log.messageStatus}
                    onClick={() => fetchJobAndOpenModal(log)}
                  />
                  {log.direction === 'OUTBOUND' && (
                    <Tooltip title={`${log.from} ${log.modifiedBy}`}>
                      {log?.recruiterName && log?.recruiterName.length > 0 ? (
                        <Avatar>{`${log.recruiterName.substring(0, 1)}`}</Avatar>
                      ) : (
                        <></>
                      )}
                    </Tooltip>
                  )}
                </Stack>
                <Stack px={8}>
                  <WsTypography color={WsTypoColors.TERTIARY}>
                    {timeSince(log.createdOn?.toString() ?? '')}
                  </WsTypography>
                </Stack>
              </Stack>
            );
          }
        })}
      <Stack alignItems={'center'} py={4}>
        {totalCount > 50 && <WsButton onClick={() => setPage(page + 1)}>Load more</WsButton>}
      </Stack>
    </LayoutTheme>
  );
};

export default SmsLogs;
